import { useState } from 'react'

import { Trans } from '@lingui/react'
import { isNil } from 'ramda'

import {
  useAddNewBankAccountMutation,
  useSelectCashBankAccountMutation,
} from '@twisto/api-graphql'
import { ButtonGroup, TextButton } from '@twisto/components/atoms/button'
import { FormattedCurrencyNumber } from '@twisto/components/atoms/format'
import {
  Headline,
  HeadlineIcon,
  HeadlineTitle,
} from '@twisto/components/atoms/headline'
import { IconCircle } from '@twisto/components/atoms/icon-circle'
import { Action } from '@twisto/components/molecules/action'
import { ActionCheckbox } from '@twisto/components/molecules/action-checkbox'
import { AddIcon, BankIcon, BigBankIcon } from '@twisto/icons'
import { styleUtils } from '@twisto/styles'

import { BankAccountsLayout } from '../../components'
import { useFlow } from '../../flow/flow.context'

import * as styles from './cash-bank-accounts-list-step.styles'
import { EligibilityBanner } from './eligibility-banner'

export const CashBankAccountsListStep = () => {
  const [selectedBankAccount, setSelectedBankAccount] = useState<string | null>(
    null
  )
  const { sessionId, flow } = useFlow<'CashBankAccountsListStep'>()

  const [selectBankAccount, { loading: selectBankLoading }] =
    useSelectCashBankAccountMutation()
  const [addNewBankAccount, { loading }] = useAddNewBankAccountMutation()
  const currentStep = flow.currentStep

  // we want to display and alow choose only verified bank accounts
  // otherwise BE will return an error on requestCash mutation
  const verifiedBankAccounts = currentStep.bankAccounts

  const handleAddNewBankAccount = () =>
    addNewBankAccount({
      variables: {
        auth: {
          sessionId,
        },
      },
    })

  const handleRequestCash = () => {
    if (!isNil(selectedBankAccount)) {
      selectBankAccount({
        variables: {
          auth: {
            sessionId,
          },
          input: {
            bankAccountId: selectedBankAccount,
          },
        },
      })
    }
  }

  return (
    <BankAccountsLayout
      end={
        <>
          {verifiedBankAccounts.map(({ id, bankAccountNumber, bankName }) => (
            <ActionCheckbox
              key={id}
              css={styles.actionButton}
              disabled={loading}
              icon={<IconCircle icon={BankIcon} />}
              isChecked={selectedBankAccount === id}
              label={bankName}
              subLabel={bankAccountNumber}
              onClick={() => setSelectedBankAccount(id)}
            />
          ))}
          <Action
            caption={
              <Trans id="flow.cashBankAccountListStep.addNewBankAccountButton" />
            }
            css={[styles.actionButton, styles.addNewAccountButton]}
            disabled={loading}
            icon={<IconCircle color="primary" icon={AddIcon} />}
            onClick={handleAddNewBankAccount}
          />
          <ButtonGroup>
            {currentStep.hasEligibilityBanner && <EligibilityBanner />}
            <TextButton
              fullWidth
              disabled={isNil(selectedBankAccount) || selectBankLoading}
              onClick={handleRequestCash}
            >
              <Trans id="flow.cashBankAccountListStep.submitButton" />
            </TextButton>
          </ButtonGroup>
        </>
      }
      start={
        <Headline>
          <HeadlineIcon>
            <BigBankIcon color="primary" height="100%" width="100%" />
          </HeadlineIcon>
          <HeadlineTitle>
            <Trans
              components={[<br key="0" />]}
              id="flow.cashBankAccountListStep.title"
              values={{
                amount: (
                  <span css={styleUtils.fontWeight.heavy}>
                    <FormattedCurrencyNumber value={currentStep.cashAmount} />
                  </span>
                ),
              }}
            />
          </HeadlineTitle>
        </Headline>
      }
    />
  )
}
